import { SpeakingLevelBadge } from "../../UI/SpeakingLevelBadge.tsx";
import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { Card } from "../../UI/Card.tsx";
import { SpeakingLevel } from "../../gql/graphql.ts";
import { SectionTitle } from "./SectionTitle.tsx";
import { StudentLevelCount } from "./useStudentPlanStatistics.ts";
import { USERS_COLOR } from "./userStatisticsColors.ts";

const SPEAKING_LEVELS_FROM_LOWEST: string[] = [
  SpeakingLevel.Starters,
  SpeakingLevel.Explorers,
  SpeakingLevel.Navigators,
  SpeakingLevel.Pioneers,
  SpeakingLevel.Voyagers,
  SpeakingLevel.Unknown,
];

function levelRank(speakingLevel: string): number {
  const rank = SPEAKING_LEVELS_FROM_LOWEST.indexOf(speakingLevel);
  return rank === -1 ? SPEAKING_LEVELS_FROM_LOWEST.length : rank;
}

function withEveryKnownLevel(
  levelCounts: StudentLevelCount[],
): StudentLevelCount[] {
  const countedLevels = new Set(
    levelCounts.map((levelCount) => levelCount.speakingLevel),
  );
  const missingLevels = SPEAKING_LEVELS_FROM_LOWEST.filter(
    (speakingLevel) => !countedLevels.has(speakingLevel),
  ).map((speakingLevel) => ({ speakingLevel, studentCount: 0 }));
  return [...levelCounts, ...missingLevels].sort(
    (first, second) =>
      levelRank(first.speakingLevel) - levelRank(second.speakingLevel),
  );
}

export function StudentsByLevel({
  levelCounts,
}: {
  levelCounts: StudentLevelCount[] | null;
}) {
  const rows = levelCounts ? withEveryKnownLevel(levelCounts) : [];
  const totalStudents = rows.reduce(
    (total, row) => total + row.studentCount,
    0,
  );
  const largestCount = Math.max(1, ...rows.map((row) => row.studentCount));

  return (
    <Card>
      <div className={"flex flex-col gap-1"}>
        <SectionTitle>Students by level</SectionTitle>
        <span className={"text-xs text-slate-400"}>
          Students with an active group or 1to1 plan, by current speaking level
        </span>
      </div>
      {levelCounts === null ? (
        <LoadingRows rows={6} rowClassName={"h-5"} />
      ) : (
        <div className={"flex flex-col gap-2.5"}>
          {rows.map((row) => {
            const share = totalStudents
              ? Math.round((row.studentCount / totalStudents) * 100)
              : 0;
            return (
              <div
                key={row.speakingLevel}
                className={
                  "grid grid-cols-[7.5rem_1fr_5.5rem] items-center gap-3 text-sm"
                }
              >
                <span>
                  <SpeakingLevelBadge level={row.speakingLevel} />
                </span>
                <div className={"h-5 rounded bg-slate-100 overflow-hidden"}>
                  <div
                    className={"h-full rounded"}
                    style={{
                      width: `${(row.studentCount / largestCount) * 100}%`,
                      backgroundColor: USERS_COLOR,
                    }}
                  />
                </div>
                <span className={"text-right text-slate-500"}>
                  <span className={"font-semibold text-slate-800"}>
                    {row.studentCount}
                  </span>{" "}
                  · {share}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
